import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';

import { useViewer } from '@hook/useViewer';
import { getFieldsSingleError } from '@utils/common';
import { notifyError } from '@utils/notifications';
import { UNKNOWN_ERROR_MESSAGE } from '@constants';
import { login } from '@urls';
import { sendSocialFeedSavePostEvent } from '@utils/analytics';

import { usePostLikeMutation, usePostUnlikeMutation } from './gql/graphql.v5.generated';

interface SocialPost {
  id: string,
  likesCount?: number,
}

export const useSocailPostLikeToggle = (post: SocialPost) => {
  const history = useHistory();
  const location = useLocation();

  const { viewer } = useViewer();

  const [likePost, { loading: likePostLoading }] = usePostLikeMutation({
    context: {
      v5: true,
    },
    optimisticResponse: () => ({
      postLike: {
        post: {
          id: post.id,
          isLiked: true,
          likesCount: post.likesCount !== undefined ? post.likesCount + 1 : 0,
          __typename: 'PostType',
        },
        __typename: 'PostLikeType',
      },
    }),
  });

  const [unlikePost, { loading: unlikePostLoading }] = usePostUnlikeMutation({
    context: {
      v5: true,
    },
    optimisticResponse: () => ({
      postUnlike: {
        post: {
          id: post.id,
          isLiked: false,
          likesCount: post.likesCount !== undefined ? post.likesCount - 1 : 0,
          __typename: 'PostType',
        },
        __typename: 'PostUnlikeType',
      },
    }),
  });

  const handleRedirectToLogin = () => {
    const loginPathname = history.createHref({
      pathname: login,
      search: queryString.stringify({
        next: location.pathname,
      }),
    });

    window.location.href = loginPathname;
  };

  const handleLikePost = async () => {
    if (viewer?.id) {
      try {
        const response = await likePost({ variables: { postId: post.id } });
  
        const errors = response.data?.postLike.errors;
  
        if (errors?.length) {
          const error = getFieldsSingleError(errors);
  
          notifyError(Object.values(error)[0]);
        } else {
          sendSocialFeedSavePostEvent();
        }
  
      } catch {
        notifyError(UNKNOWN_ERROR_MESSAGE());
      }
    } else {
      handleRedirectToLogin();
    }
  };

  const handleUnlikePost = async () => {
    if (viewer?.id) {
      try {
        const response = await unlikePost({ variables: { postId: post.id } });
  
        const errors = response.data?.postUnlike.errors;
  
        if (errors?.length) {
          const error = getFieldsSingleError(errors);
  
          notifyError(Object.values(error)[0]);
        }
  
      } catch {
        notifyError(UNKNOWN_ERROR_MESSAGE());
      }
    } else {
      handleRedirectToLogin();
    }
  };

  return {
    handleLikePost,
    handleUnlikePost,
    isLoading: likePostLoading || unlikePostLoading,
  };
};
